<div class="max-w-5xl">
  <!-- Loader -->
  @if (isLoading()) {
    <div class="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="mb-5 h-5 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-800"></div>
      @for (i of [1, 2, 3, 4, 5]; track i) {
        <div class="mb-3 h-4 w-full animate-pulse rounded bg-gray-100 dark:bg-gray-800/70"></div>
      }
    </div>
  } @else {
  <form [formGroup]="form" (ngSubmit)="saveSchedule()">

    @if (hasDefaultSchedules()) {
      <div class="mb-5 flex gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-theme-sm text-brand-800
        dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-200">
        <svg class="mt-0.5 size-5 shrink-0 text-brand-500" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.75Z" />
        </svg>
        <p>
          Aún no has guardado tus horarios. Te sugerimos lunes a viernes de 9:00 a 18:00;
          ajústalo a tu negocio y guarda para que tus agendas lo usen.
        </p>
      </div>
    }

    <!-- Week overview -->
    <section class="mb-5 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 dark:border-gray-800 dark:bg-white/[0.03]"
      aria-labelledby="bh-week-title">
      <div class="mb-5 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h2 id="bh-week-title" class="text-lg font-semibold text-gray-800 dark:text-white/90">Tu semana</h2>
        <p class="text-theme-sm text-gray-500 dark:text-gray-400">
          @if (weekSummary.openDays === 0) {
            Todos los días están cerrados
          } @else {
            Abierto {{ weekSummary.openDays }} {{ weekSummary.openDays === 1 ? 'día' : 'días' }},
            <span class="font-medium text-gray-800 dark:text-white/90">{{ weekSummary.hours }}</span> en total
          }
        </p>
      </div>

      <div class="bh-timeline">
        <!-- Hour axis -->
        <div class="bh-timeline__row" aria-hidden="true">
          <span></span>
          <div class="relative h-5">
            @for (tick of timelineTicks; track tick.label) {
              <span class="bh-tick-label" [style.left.%]="tick.left">{{ tick.label }}</span>
            }
          </div>
        </div>

        @for (day of daysOfWeek; track day.value) {
          <button type="button" class="bh-timeline__row group" (click)="scrollToDay(day.value)"
            [attr.aria-label]="'Ir a ' + day.label">
            <span class="bh-day-label text-left text-theme-sm font-medium" [class.bh-off]="!isDayOpen(day.value)">
              {{ day.short }}
            </span>
            <div class="bh-track">
              @for (tick of timelineTicks; track tick.label) {
                <span class="bh-gridline" [style.left.%]="tick.left"></span>
              }
              @for (seg of getTimelineSegments(day.value); track $index) {
                <span class="bh-segment" [style.left.%]="seg.left" [style.width.%]="seg.width" [title]="seg.label"></span>
              }
              @if (!isDayOpen(day.value)) {
                <span class="absolute inset-y-0 left-2 flex items-center text-theme-xs text-gray-400 dark:text-gray-500">Cerrado</span>
              }
            </div>
          </button>
        }
      </div>
    </section>

    <!-- Day editor -->
    <section class="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]"
      aria-label="Horario por día">
      @for (day of daysOfWeek; track day.value) {
        @let open = isDayOpen(day.value);
        <div [id]="'bh-day-' + day.value" class="bh-day scroll-mt-24">
          <div class="grid grid-cols-[1fr_auto] items-start gap-x-4 gap-y-3 px-5 py-4 sm:px-6 md:grid-cols-[11rem_1fr_auto]">

            <!-- Day + switch -->
            <div class="flex h-10 items-center gap-3">
              <button type="button" role="switch" class="bh-switch" [attr.aria-checked]="open"
                [attr.aria-label]="(open ? 'Cerrar ' : 'Abrir ') + day.label" (click)="toggleDay(day.value)">
                <span class="bh-switch__thumb"></span>
              </button>
              <span class="bh-day-name text-theme-sm font-semibold" [class.bh-off]="!open">
                {{ day.label }}
              </span>
            </div>

            <!-- Day actions (top-right on mobile, right column on desktop) -->
            <div class="flex h-10 items-center justify-end gap-1 md:order-last">
              @if (open) {
                <span class="mr-2 hidden text-theme-xs text-gray-500 tabular-nums lg:inline dark:text-gray-400">
                  {{ formatDuration(getDayMinutes(day.value)) }}
                </span>
                <button type="button" class="bh-icon-btn" (click)="addRangeForDay(day.value)"
                  [attr.aria-label]="'Agregar otro horario al ' + day.label" title="Agregar otro horario">
                  <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </button>
              }
              <button type="button" class="bh-icon-btn" (click)="openCopy(day.value)"
                [class.bh-icon-btn--active]="copySourceDay() === day.value"
                [attr.aria-expanded]="copySourceDay() === day.value"
                [attr.aria-label]="'Copiar el horario del ' + day.label + ' a otros días'" title="Copiar a otros días">
                <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
                </svg>
              </button>
            </div>

            <!-- Ranges -->
            <div class="col-span-2 md:col-span-1">
              @if (!open) {
                <p class="flex h-10 items-center text-theme-sm text-gray-400 dark:text-gray-500">
                  Cerrado. Activa el interruptor para recibir citas este día.
                </p>
              } @else {
                <div class="space-y-2">
                  @for (item of getSchedulesForDay(day.value); track item.group) {
                    @if (item.group.get('isActive')?.value) {
                      @let error = getRangeError(day.value, item.index);
                      <div>
                        <div class="flex items-center gap-2">
                          <input type="time" class="bh-time" [class.bh-time--error]="error"
                            [formControl]="$any(item.group.get('startTime'))"
                            [attr.aria-label]="'Apertura del ' + day.label">
                          <span class="text-theme-sm text-gray-400">a</span>
                          <input type="time" class="bh-time" [class.bh-time--error]="error"
                            [formControl]="$any(item.group.get('endTime'))"
                            [attr.aria-label]="'Cierre del ' + day.label">
                          <button type="button" class="bh-icon-btn bh-icon-btn--danger" (click)="removeScheduleRange(item.index)"
                            [attr.aria-label]="'Quitar este horario del ' + day.label" title="Quitar horario">
                            <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        @if (error) {
                          <p class="mt-1 text-theme-xs text-error-600 dark:text-error-400" role="alert">{{ error }}</p>
                        }
                      </div>
                    }
                  }
                </div>
              }
            </div>
          </div>

          <!-- Copy to other days -->
          @if (copySourceDay() === day.value) {
            <div class="bh-copy border-t border-gray-100 bg-gray-50 px-5 py-4 sm:px-6 dark:border-gray-800 dark:bg-white/[0.02]">
              <p class="mb-3 text-theme-sm text-gray-700 dark:text-gray-300">
                Copiar el horario del {{ day.label.toLowerCase() }} a:
              </p>
              <div class="flex flex-wrap gap-2">
                @for (target of daysOfWeek; track target.value) {
                  @if (target.value !== day.value) {
                    <button type="button" class="bh-chip" [attr.aria-pressed]="copyTargets().has(target.value)"
                      (click)="toggleCopyTarget(target.value)">
                      {{ target.label }}
                    </button>
                  }
                }
              </div>
              <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
                <div class="flex gap-3 text-theme-sm">
                  <button type="button" class="font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400"
                    (click)="selectCopyTargets('weekdays')">Lunes a viernes</button>
                  <button type="button" class="font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400"
                    (click)="selectCopyTargets('all')">Toda la semana</button>
                </div>
                <div class="flex gap-2">
                  <button type="button" class="bh-btn bh-btn--ghost" (click)="closeCopy()">Cancelar</button>
                  <button type="button" class="bh-btn bh-btn--primary" [disabled]="copyTargets().size === 0" (click)="applyCopy()">
                    Copiar
                  </button>
                </div>
              </div>
            </div>
          }
        </div>
      }
    </section>

    <!-- Save bar -->
    <div class="bh-savebar" [class.bh-savebar--dirty]="form.dirty">
      <p class="bh-savebar__status text-theme-sm">
        @if (form.dirty) {
          Tienes cambios sin guardar
        } @else {
          Horarios guardados
        }
      </p>
      <div class="flex gap-2">
        <button type="button" class="bh-btn bh-btn--ghost" [disabled]="!form.dirty || isSaving()" (click)="discardChanges()">
          Descartar
        </button>
        <button type="submit" class="bh-btn bh-btn--primary" [disabled]="isSaving() || !form.dirty || hasRangeErrors()">
          {{ isSaving() ? 'Guardando…' : 'Guardar horarios' }}
        </button>
      </div>
    </div>
  </form>
  }
</div>
